import { useCallback, useMemo, useState } from "react";
import type { FailureMode } from "../types";
import { DEFAULT_VISIBLE_COUNT, topModeIds } from "../utils/visibleModes";

export interface UseVisibleModesResult {
  /** Ids of the modes currently drawn on the map. */
  visibleModeIds: Set<string>;
  /** Show a mode if hidden, hide it if shown. */
  toggle: (id: string) => void;
  /** Show a mode if it isn't already (used when selecting a hidden mode). */
  ensureVisible: (id: string) => void;
  /** Draw every mode. */
  showAll: () => void;
  /** Draw no modes. */
  hideAll: () => void;
  /** Back to the default — the largest `DEFAULT_VISIBLE_COUNT` modes. */
  resetTop: () => void;
}

/**
 * Tracks which failure modes the map draws (design doc 015 §6). Defaults to the
 * largest few; the legend toggles the rest. Visibility is a separate axis from
 * selection (`useSelectedMode`): selection *emphasises* one of the visible
 * modes, visibility decides which modes are drawn at all.
 *
 * The set resets to the top-N default whenever the underlying mode set changes —
 * scrubbing the time slider or switching daily/weekly — so a manual selection
 * from one window never carries over as a stale filter into another. Within a
 * window (re-fetches that return the same modes) the user's toggles are kept.
 */
export function useVisibleModes(modes: FailureMode[]): UseVisibleModesResult {
  // Identifies the current mode set by content, not array identity, so a refetch
  // returning the same modes does not clobber the user's toggles.
  const modesKey = useMemo(
    () =>
      modes
        .map((m) => m.failure_mode_id)
        .sort()
        .join(","),
    [modes],
  );

  const [visibleModeIds, setVisibleModeIds] = useState<Set<string>>(() =>
    topModeIds(modes, DEFAULT_VISIBLE_COUNT),
  );
  // Reset on mode-set change via the "adjust state during render" pattern (React
  // docs) rather than an effect, so the map never flashes the old window's modes.
  const [seenKey, setSeenKey] = useState(modesKey);
  if (modesKey !== seenKey) {
    setSeenKey(modesKey);
    setVisibleModeIds(topModeIds(modes, DEFAULT_VISIBLE_COUNT));
  }

  const toggle = useCallback((id: string) => {
    setVisibleModeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const ensureVisible = useCallback((id: string) => {
    setVisibleModeIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const showAll = useCallback(
    () => setVisibleModeIds(new Set(modes.map((m) => m.failure_mode_id))),
    [modes],
  );
  const hideAll = useCallback(() => setVisibleModeIds(new Set()), []);
  const resetTop = useCallback(
    () => setVisibleModeIds(topModeIds(modes, DEFAULT_VISIBLE_COUNT)),
    [modes],
  );

  return { visibleModeIds, toggle, ensureVisible, showAll, hideAll, resetTop };
}
